import type { AdminSettings, AdminUserItem, Lang } from '@fonus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Field, inputClass, ListSkeleton, PageTitle } from '../components/common.tsx';
import { ErrorCard } from '../components/ErrorCard.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.tsx';
import { api } from '../lib/api.ts';
import { humanFromApi } from '../lib/errors.ts';
import { credits, LANG_LABEL, num } from '../lib/format.ts';
import { useSettings } from '../lib/queries.ts';

const TZ = ' (по Ташкенту)';
const LANGS: Lang[] = ['uz', 'ru', 'en'];

function Slider({
  label,
  left,
  right,
  value,
  disabled,
  onChange,
}: {
  label: string;
  left: string;
  right: string;
  value: number;
  disabled: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-[14px]">
      <span className="font-medium">
        {label} · {value.toFixed(2).replace('.', ',')}
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        disabled={disabled}
        aria-label={label}
        className="accent-[#FF8A3D]"
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="flex justify-between text-[12px] text-muted">
        <span>{left}</span>
        <span>{right}</span>
      </span>
    </label>
  );
}

function SettingsForm({ initial, isOwner }: { initial: AdminSettings; isOwner: boolean }) {
  const qc = useQueryClient();
  const [d, setD] = useState(initial);
  const [fxManual, setFxManual] = useState(
    initial.fxManual === null ? '' : String(initial.fxManual),
  );
  const save = useMutation({
    mutationFn: () =>
      api('/admin/settings', {
        method: 'PATCH',
        body: {
          claudeModelGenerate: d.claudeModelGenerate,
          claudeModelReview: d.claudeModelReview,
          ttsMonthlyCreditLimit: d.ttsMonthlyCreditLimit,
          autoGenerateEnabled: d.autoGenerateEnabled,
          generateTime: d.generateTime,
          defaultPublishTime: d.defaultPublishTime,
          reviewMinScore: d.reviewMinScore,
          broadcastTime: d.broadcastTime,
          elevenLabsCreditUsd: d.elevenLabsCreditUsd,
          fxManual: fxManual.trim() ? Number(fxManual.replace(',', '.')) : null,
          voiceSettings: d.voiceSettings,
          autoTags: d.autoTags,
        },
      }),
    onSuccess: () => {
      toast.success('Настройки сохранены');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  const set = <K extends keyof AdminSettings>(k: K, v: AdminSettings[K]) => setD({ ...d, [k]: v });
  const off = !isOwner;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Голос и эмоции</CardTitle>
          <CardDescription>
            Для каждого языка отдельно. Подобрав значения, послушайте пробную озвучку абзаца на
            странице сказки (вкладка «Сценарий озвучки»). Модель eleven_v3 округляет стабильность до
            0 / 0,5 / 1.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-3">
          {LANGS.map((l) => {
            const v = d.voiceSettings[l];
            const setV = (patch: Partial<typeof v>) =>
              set('voiceSettings', { ...d.voiceSettings, [l]: { ...v, ...patch } });
            return (
              <div key={l} className="flex flex-col gap-4 rounded-[16px] border border-line p-4">
                <p className="font-semibold">{LANG_LABEL[l]}</p>
                <Slider
                  label="Стабильность"
                  left="эмоциональнее"
                  right="стабильнее"
                  value={v.stability}
                  disabled={off}
                  onChange={(x) => setV({ stability: x })}
                />
                <Slider
                  label="Выразительность"
                  left="ровнее"
                  right="выразительнее"
                  value={v.style}
                  disabled={off}
                  onChange={(x) => setV({ style: x })}
                />
                <label className="flex items-start gap-2 text-[14px]">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-[#FF8A3D]"
                    checked={d.autoTags[l]}
                    disabled={off}
                    onChange={(e) => set('autoTags', { ...d.autoTags, [l]: e.target.checked })}
                  />
                  <span>
                    Автотеги при озвучке
                    <span className="block text-[12px] text-muted">
                      Claude расставит теги эмоций, если у языка нет своего сценария.
                    </span>
                  </span>
                </label>
              </div>
            );
          })}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Контент</CardTitle>
          <CardDescription>
            ElevenLabs за месяц: {credits(d.ttsMonthUsed)} из {num(d.ttsMonthlyCreditLimit)}.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Модель генерации">
            <select
              className={inputClass}
              value={d.claudeModelGenerate}
              disabled={off}
              onChange={(e) => set('claudeModelGenerate', e.target.value)}
            >
              {d.claudeModels.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Модель рецензии">
            <select
              className={inputClass}
              value={d.claudeModelReview}
              disabled={off}
              onChange={(e) => set('claudeModelReview', e.target.value)}
            >
              {d.claudeModels.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Месячный лимит ElevenLabs, кредитов">
            <input
              type="number"
              min={0}
              className={inputClass}
              value={d.ttsMonthlyCreditLimit}
              disabled={off}
              onChange={(e) => set('ttsMonthlyCreditLimit', Number(e.target.value))}
            />
          </Field>
          <Field
            label="Порог оценки рецензии"
            hint="Ниже — сказка помечается для внимательной проверки."
          >
            <input
              type="number"
              min={0}
              max={100}
              className={inputClass}
              value={d.reviewMinScore}
              disabled={off}
              onChange={(e) => set('reviewMinScore', Number(e.target.value))}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Расписание</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label={`Публикация по умолчанию${TZ}`}>
            <input
              type="time"
              className={inputClass}
              value={d.defaultPublishTime}
              disabled={off}
              onChange={(e) => set('defaultPublishTime', e.target.value)}
            />
          </Field>
          <Field label={`Вечерняя рассылка${TZ}`}>
            <input
              type="time"
              className={inputClass}
              value={d.broadcastTime}
              disabled={off}
              onChange={(e) => set('broadcastTime', e.target.value)}
            />
          </Field>
          <Field
            label={`Ежедневная генерация${TZ}`}
            hint={
              d.autoGenerateEnabled
                ? 'Каждый день в это время создаётся одна сказка.'
                : 'Выключена — только кнопка «Новая сказка».'
            }
          >
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-[14px]">
                <input
                  type="checkbox"
                  className="size-4 accent-[#FF8A3D]"
                  checked={d.autoGenerateEnabled}
                  disabled={off}
                  onChange={(e) => set('autoGenerateEnabled', e.target.checked)}
                />{' '}
                вкл.
              </label>
              <input
                type="time"
                className={inputClass}
                value={d.generateTime}
                disabled={off || !d.autoGenerateEnabled}
                onChange={(e) => set('generateTime', e.target.value)}
              />
            </div>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Стоимость</CardTitle>
          <CardDescription>
            {d.fx
              ? d.fx.source === 'cbu'
                ? `Курс ЦБ Узбекистана: 1 $ = ${num(d.fx.rate)} сум на ${d.fx.date} (обновляется раз в сутки).`
                : `Курс задан вручную: 1 $ = ${num(d.fx.rate)} сум.`
              : 'Курс ЦБ ещё не загружен — суммы в сумах появятся после первой загрузки.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field
            label="Цена 1 кредита ElevenLabs, $"
            hint="Starter: $5 за 30 000. После перехода на Pro обновите."
          >
            <input
              type="number"
              step="0.00001"
              min={0}
              className={inputClass}
              value={d.elevenLabsCreditUsd}
              disabled={off}
              onChange={(e) => set('elevenLabsCreditUsd', Number(e.target.value))}
            />
          </Field>
          <Field label="Курс вручную, сум за $" hint="Пусто — используется курс ЦБ.">
            <input
              inputMode="decimal"
              className={inputClass}
              value={fxManual}
              disabled={off}
              placeholder={d.fx?.source === 'cbu' ? String(d.fx.rate) : ''}
              onChange={(e) => setFxManual(e.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      {isOwner && (
        <div className="flex items-center gap-3">
          <Button size="lg" disabled={save.isPending} onClick={() => save.mutate()}>
            Сохранить
          </Button>
          {save.error && <ErrorCard error={humanFromApi(save.error)} />}
        </div>
      )}
    </>
  );
}

function Admins() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admins'],
    queryFn: () => api<{ items: AdminUserItem[] }>('/admin/admins'),
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; receivesReports: boolean }) =>
      api(`/admin/admins/${v.id}`, {
        method: 'PATCH',
        body: { receivesReports: v.receivesReports },
      }),
    onSuccess: () => {
      toast.success('Сохранено');
      void qc.invalidateQueries({ queryKey: ['admins'] });
    },
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Админы</CardTitle>
        <CardDescription>
          Отчёт в 09:00 по Ташкенту и оповещения о сбоях получают отмеченные.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Имя</TableHead>
              <TableHead>Роль</TableHead>
              <TableHead>Отчёт и оповещения</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.items.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  {a.name} {a.username ? `@${a.username}` : ''}{' '}
                  <span className="text-[12px] text-muted">{a.telegramId}</span>
                </TableCell>
                <TableCell>
                  {a.role === 'OWNER' ? 'Владелец' : a.role === 'EDITOR' ? 'Редактор' : 'Просмотр'}
                </TableCell>
                <TableCell>
                  <input
                    type="checkbox"
                    className="size-4 accent-[#FF8A3D]"
                    checked={a.receivesReports}
                    aria-label={`Отчёт для ${a.name}`}
                    onChange={(e) => toggle.mutate({ id: a.id, receivesReports: e.target.checked })}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function Settings({ isOwner }: { isOwner: boolean }) {
  const q = useSettings();
  return (
    <div className="flex flex-col gap-5">
      <PageTitle
        title="Настройки"
        subtitle={isOwner ? undefined : 'Менять настройки может только владелец.'}
      />
      {q.isLoading && <ListSkeleton rows={3} />}
      {q.error && <ErrorCard error={humanFromApi(q.error)} />}
      {q.data && <SettingsForm key={JSON.stringify(q.data)} initial={q.data} isOwner={isOwner} />}
      {isOwner && <Admins />}
    </div>
  );
}
